import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

const FAQS = [
  {
    question: 'What is the best property management file organization tool?',
    answer:
      "Sortio is built for the property management document mix: per-unit folders, lease lifecycle docs, maintenance reports, vendor invoices, owner statements, and tenant ledgers. It runs on Mac and Windows, classifies files locally, and is shipping native integrations with Buildium and Rent Manager so files routed to a property upload into the matching unit, lease, or work order entity.",
  },
  {
    question: 'Does Sortio integrate with Buildium?',
    answer:
      "The Buildium integration is shipping in the next Sortio release alongside Rent Manager, dotloop, and Zotero. It uses Buildium's API key flow and lists your properties, units, leases, and work orders for the container picker so a file routed to a property in Sortio uploads to the matching Buildium entity. Until then Sortio works alongside Buildium via the filesystem: organize the files cleanly on disk, then upload through the Buildium UI.",
  },
  {
    question: 'Does Sortio automate Rent Manager file workflows?',
    answer:
      "Yes, with the Rent Manager integration shipping next. It uses the rmAPI / WAPI12 partner program with token-based auth, lists tenants, properties, units, and work orders, and supports signed-URL attachment uploads with checksum validation. For property managers on Rent Manager today, Sortio works against the local files Rent Manager exports or syncs and routes them into per-unit folders.",
  },
  {
    question: 'How does Sortio organize per-unit folders?',
    answer:
      "Drop a folder of mixed property documents into Sortio. It extracts the property address, unit number, tenant name, lease dates, and document type from each file, then routes into Property / Unit / Lease subfolders. A maintenance work order at 1127 Cornell Apt 4B gets recognized as a separate entity from Apt 4A, and the unit's lease history stays intact as tenants turn over.",
  },
  {
    question: 'How does Sortio handle the lease lifecycle (application through move-out)?',
    answer:
      "Each lease gets its own folder under the unit, with subfolders for application and screening, signed lease and addenda, renewals, violations and notices, ledger and payments, and move-out. As a tenant moves through that lifecycle, Sortio routes new documents into the right phase folder. When the lease ends, the whole lease folder packs into an archive labeled with the move-out date and the security deposit disposition.",
  },
  {
    question: 'Can Sortio prep year-end 1099-NEC routing for vendors?',
    answer:
      "Yes. Sortio recognizes recurring vendors (plumbers, landscapers, cleaning crews, snow removal) and routes invoices by vendor and property. At year end, the same routing produces a per-vendor folder with all invoices for the tax year, which makes 1099-NEC preparation a one-folder export to your CPA. See the accountants page for the deeper pattern.",
  },
  {
    question: 'How does Sortio help with regulatory retention?',
    answer:
      "The Rule Builder lets you encode state-specific retention rules (security deposit records, eviction filings, Fair Housing inquiries) and tags each closed lease folder with a retention class. When the retention window expires, the same rule surfaces archives for deletion review. Sortio never deletes files automatically; it gives you the inventory so retention decisions are auditable.",
  },
  {
    question: 'Will tenant Social Security numbers or financial info leave my machine?',
    answer:
      "Not unless you choose the cloud classifier. In Ollama mode no document content (and no filenames) crosses the network. In BYOK mode requests go from your machine directly to your provider on your account. With the Sortio default classifier, filenames and extracted entities are sent for matching but redacted from logs after 30 days. For tenant screening reports with SSNs, we recommend Ollama or BYOK.",
  },
  {
    question: 'Does Sortio work with AppFolio, Yardi, or Propertyware?',
    answer:
      "Filesystem mode works with any platform that writes documents to disk or exports them as PDF. Native AppFolio integration is on the Tier 2 roadmap once Stack partner BD clears. Yardi Voyager and Yardi Breeze are deferred (Yardi Breeze requires 3 mutual clients before partner status, Yardi Voyager is enterprise-sales-led). Propertyware requires a paying customer on the $1/unit/month API tier to justify the build.",
  },
  {
    question: 'How fast is it on a 60-file weekly intake of new leases, work orders, and invoices?',
    answer:
      "AI Sort completes a 60-file weekly intake in under a minute on Pro on a modern Mac. For larger historical archives (a multi-year backlog of 30,000 maintenance reports), use the Rule Builder. Rule-based sorts process unlimited files instantly and never touch your AI allowance.",
  },
  {
    question: 'Can a small property manager with 30 units run Sortio solo?',
    answer:
      "Yes, that is exactly the Pro target. $14.99 per month or $99 per year includes the per-unit routing, a monthly AI allowance sized for heavy use, BYOK, and the Buildium and Rent Manager integrations when they ship. For larger operations, the Team plan ($29 per seat per month) adds a shared rule library across leasing agents and bookkeepers.",
  },
  {
    question: 'Does it work on Windows or only Mac?',
    answer:
      "Sortio ships for macOS, Windows, and Linux. Most property management shops we work with are on Windows; some are on Mac with a Windows VM for legacy software. The Buildium and Rent Manager integrations work identically on both platforms.",
  },
];

const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="mb-4 transition-all duration-300 transform hover:translate-x-1">
    <div className={`apple-panel transition-all duration-300 ${isOpen ? 'ring-1 ring-sortio-primary/30' : ''}`}>
      <button className="flex justify-between items-center w-full p-5 text-left rounded-apple focus:outline-none" onClick={onToggle} aria-expanded={isOpen}>
        <span className="text-base font-medium text-apple-50 pr-4">{question}</span>
        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors duration-300 ${isOpen ? 'bg-sortio-primary/20' : 'bg-apple-600/40'}`}>
          {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-sortio-primary" /> : <ChevronDown className="w-3.5 h-3.5 text-apple-300" />}
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="p-5 pt-0">
          <p className="text-apple-300 text-sm leading-relaxed">{answer}</p>
        </div>
      </div>
    </div>
  </div>
);

const PropertyFAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };

  return (
    <section id="property-faq" className="my-20">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="flex flex-col items-center mb-12">
        <div className="mb-4 rounded-full bg-apple-700/60 backdrop-blur-sm p-2 border border-apple-600/20">
          <HelpCircle className="w-6 h-6 text-sortio-primary" />
        </div>
        <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight text-apple-50 text-center">
          Property management <span className="apple-gradient-text">FAQ</span>
        </h2>
        <p className="text-apple-300 text-lg font-light max-w-2xl text-center">
          Questions from independent property managers, multifamily operators, and HOA managers
          evaluating Sortio for per-unit filing and platform integration.
        </p>
      </div>

      <div className="max-w-3xl mx-auto">
        {FAQS.map((faq, index) => (
          <FAQItem key={index} question={faq.question} answer={faq.answer} isOpen={openIndex === index} onToggle={() => setOpenIndex(openIndex === index ? -1 : index)} />
        ))}
      </div>
    </section>
  );
};

export default PropertyFAQ;
